import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEye, FiStar, FiCircle, FiZap, FiArrowRight, FiAward, FiCheckCircle, FiX } = FiIcons;

const GradingSection = ({ nextStep, t }) => {
  const [selectedC, setSelectedC] = useState('cut');
  const [quizStep, setQuizStep] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const fourCs = {
    cut: {
      title: t('cut'),
      icon: FiZap,
      description: t('cutDesc'),
      grades: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'],
      color: 'from-pantone-blue to-cyan-500'
    },
    clarity: {
      title: t('clarity'),
      icon: FiEye,
      description: t('clarityDesc'),
      grades: ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2'],
      color: 'from-green-500 to-emerald-500'
    },
    color: {
      title: t('color'),
      icon: FiCircle,
      description: t('colorDesc'),
      grades: ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'],
      color: 'from-purple-500 to-violet-500'
    },
    carat: {
      title: t('carat'),
      icon: FiStar,
      description: t('caratDesc'),
      grades: ['0.5ct', '1.0ct', '1.5ct', '2.0ct', '3.0ct+'],
      color: 'from-amber-500 to-orange-500'
    }
  };

  const quizQuestions = [
    {
      question: t('quiz1'),
      options: t('quiz1Options'),
      correct: 0
    },
    {
      question: t('quiz2'),
      options: t('quiz2Options'),
      correct: 1
    },
    {
      question: t('quiz3'),
      options: t('quiz3Options'),
      correct: 1
    }
  ];

  const handleQuizAnswer = (answerIndex) => {
    const newUserAnswers = [...userAnswers, answerIndex];
    setUserAnswers(newUserAnswers);
    
    if (answerIndex === quizQuestions[quizStep].correct) {
      setScore(score + 1);
    }
    
    if (quizStep < quizQuestions.length - 1) {
      setQuizStep(quizStep + 1);
    } else {
      // Quiz completed - show results
      setShowResults(true);
    }
  };

  const resetQuiz = () => {
    setQuizStep(0);
    setScore(0);
    setUserAnswers([]);
    setShowResults(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-pantone-blue bg-clip-text text-transparent">
            {t('gradingTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('gradingDescription')}
          </p>
        </motion.div>

        {/* 4Cs Interactive Display */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* 4Cs Navigation */}
          <div>
            <h2 className="text-3xl font-semibold mb-8 text-center">{t('exploreCharacteristics')}</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(fourCs).map(([key, c]) => (
                <motion.button
                  key={key}
                  onClick={() => setSelectedC(key)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-6 rounded-xl border transition-all duration-300 ${
                    selectedC === key
                      ? `bg-gradient-to-r ${c.color} border-white/30 text-white`
                      : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <SafeIcon icon={c.icon} className="w-8 h-8 mx-auto mb-3" />
                  <h3 className="text-xl font-semibold">{c.title}</h3>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Selected C Details */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedC}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center mb-6">
                <div className={`bg-gradient-to-r ${fourCs[selectedC].color} p-4 rounded-xl mr-4`}>
                  <SafeIcon icon={fourCs[selectedC].icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white">{fourCs[selectedC].title}</h3>
              </div>
              
              <p className="text-lg text-gray-300 mb-8">{fourCs[selectedC].description}</p>
              
              <div>
                <h4 className="text-xl font-semibold mb-4 text-white">{t('gradingScale')}</h4>
                <div className="flex flex-wrap gap-2">
                  {fourCs[selectedC].grades.map((grade, index) => (
                    <motion.span
                      key={grade}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className={`px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r ${fourCs[selectedC].color} text-white`}
                    >
                      {grade}
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Interactive Quiz */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-pantone-blue/20 to-blue-600/20 backdrop-blur-sm border border-pantone-blue/20 rounded-2xl p-8 mb-12"
        >
          <div className="text-center mb-8">
            <SafeIcon icon={FiAward} className="w-12 h-12 text-pantone-blue/80 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white">{t('testKnowledge')}</h3>
            <p className="text-gray-300">{t('testDescription')}</p>
          </div>

          {!showResults && quizStep < quizQuestions.length ? (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <div className="bg-white/10 rounded-full h-2 mb-4">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${((quizStep + 1) / quizQuestions.length) * 100}%` }}
                    className="bg-gradient-to-r from-pantone-blue to-blue-500 h-full rounded-full"
                  />
                </div>
                <p className="text-center text-sm text-gray-400">
                  Question {quizStep + 1} of {quizQuestions.length}
                </p>
              </div>

              <h4 className="text-xl font-semibold text-white mb-6 text-center">
                {quizQuestions[quizStep].question}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quizQuestions[quizStep].options.map((option, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleQuizAnswer(index)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white/10 hover:bg-pantone-blue/20 border border-white/20 rounded-xl p-4 text-white transition-all duration-300"
                  >
                    {option}
                  </motion.button>
                ))}
              </div>
            </div>
          ) : showResults ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h4 className="text-2xl font-semibold text-white mb-4">{t('quizComplete')}</h4>
              <p className="text-xl text-pantone-blue/80 mb-6">
                {t('yourScore')} {score} / {quizQuestions.length}
              </p>
              
              {/* Show correct answers */}
              <div className="text-left bg-white/5 rounded-xl p-6 mb-6">
                <h5 className="text-lg font-semibold text-white mb-4">{t('correctAnswers')}</h5>
                {quizQuestions.map((question, qIndex) => (
                  <div key={qIndex} className="mb-4 p-4 bg-white/5 rounded-lg">
                    <p className="text-white font-medium mb-2">{question.question}</p>
                    <div className="flex items-center space-x-2">
                      {userAnswers[qIndex] === question.correct ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400" />
                      ) : (
                        <SafeIcon icon={FiX} className="w-5 h-5 text-red-400" />
                      )}
                      <span className={`${userAnswers[qIndex] === question.correct ? 'text-green-300' : 'text-red-300'}`}>
                        {userAnswers[qIndex] === question.correct ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <p className="text-pantone-blue/80 mt-1">
                      Correct answer: {question.options[question.correct]}
                    </p>
                    {userAnswers[qIndex] !== question.correct && (
                      <p className="text-gray-400 text-sm">
                        Your answer: {question.options[userAnswers[qIndex]]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={resetQuiz}
                  className="bg-pantone-blue/20 hover:bg-pantone-blue/30 border border-pantone-blue/40 text-white px-6 py-3 rounded-lg transition-all duration-300"
                >
                  Try Again
                </button>
              </div>
              
              <p className="text-gray-400 mt-4">{t('greatJob')}</p>
            </motion.div>
          ) : null}
        </motion.div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <button
            onClick={nextStep}
            className="group bg-gradient-to-r from-emerald-600 to-pantone-blue hover:from-pantone-blue hover:to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            {t('nextCutting')}
            <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default GradingSection;